/*
 Provides Props:
 ------------------------------------
 state.userGeo // Object
 * state: // One of: 'prompt', 'denied', 'granted'
 * lon, lat,  // Number: decimal geolocation coordinates
 * lon_pct, lat_pct, // Number proportional offset relative to the state.forecastData.geo box
 * onMap,  // Boolean indicating if lon_pct and lat_pct are both between 0 and 1 inclusive

 Actions:
 --------
 state.requestGeoPermission()

 Responds to:
 --------
 * 'init' - by running _checkGeoPermission()
 * 'forecastdata' - by running _setUserGeo() to refresh
 state.userGeo's lon_pct, lat_pct and onMap
 based on the new forecastData.geo info
 */
import {mercator} from "../utils";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export default function (state, widget) {
    const m = widget.m;

    const permissionsApi = navigator.permissions;
    const geoApi = navigator.geolocation;
    const localStorage = window.localStorage;
    let watchingGeo = false;

    /** Refresh user location. Optionally takes Position.coordinates object to update location. */
    const _setUserGeo = function (coords) {
        const userGeo = state.userGeo;

        // Store any coordinates we get, regardless of whether we display them yet.
        if (coords) {
            userGeo.lat = coords.latitude;
            userGeo.lon = coords.longitude;
        }

        // If we have co-ordinates and forecast data, try to display location
        if (state.userGeo.lat !== undefined && state.userGeo.lon !== undefined && state.forecastData) {
            const mapGeo = state.forecastData.geo;
            userGeo.lat_pct = (mapGeo.merc_n_lat - mercator(userGeo.lat)) / mapGeo.merc_sn_lat;
            userGeo.lon_pct = (userGeo.lon - mapGeo.w_lon) / mapGeo.we_lon;
            userGeo.onMap = (0 <= userGeo.lon_pct && userGeo.lon_pct <= 1) && (0 <= userGeo.lat_pct && userGeo.lat_pct <= 1);
        } else {
            userGeo.onMap = false;
        }
    };

    const _checkGeoPermission = function () {
        if (permissionsApi) {
            permissionsApi.query({name: 'geolocation'}).then(function (geoPermission) {
                const updateUserGeo = function (skipRedraw) {
                    const currentState = this.state;
                    if (currentState === 'granted') {
                        state.requestGeoPermission();
                    }
                    else {
                        watchingGeo = false;
                        state.userGeo = {state: this.state};
                    }
                    !skipRedraw && m.redraw();
                };
                updateUserGeo.call(geoPermission, true);
                geoPermission.onchange = updateUserGeo;
            });
        }
        else if (localStorage) {
            const geoPermission = localStorage.geoPermission;
            if (geoPermission) {
                const localStorageMaxAge = 2 * DAY_MS; // two days
                if (geoPermission - Date.now() < localStorageMaxAge) {
                    state.requestGeoPermission();
                }
            }
        }
    };

    // ------------------------------------------------

    state.userGeo = {
        state: geoApi ? 'prompt' : 'denied',
    };

    // ------------------------------------------------

    state.requestGeoPermission = function () {
        if (geoApi && !watchingGeo) {
            state.userGeo = {state: 'waiting',};
            geoApi.watchPosition(
                function (pos) {
                    watchingGeo = true;
                    state.userGeo.state = 'granted';
                    _setUserGeo(pos.coords);
                    m.redraw();
                    // Save geoPermission.state for older browsers
                    if (!permissionsApi && localStorage) {
                        localStorage.geoPermission = Date.now();
                    }
                },
                function () {
                    state.userGeo = {state: 'denied',};
                    m.redraw();
                    if (!permissionsApi && localStorage) {
                        localStorage.geoPermission = undefined;
                    }
                },
                {maximumAge: 3 * MINUTE_MS,}
            );
        }
    };

    // ------------------------------------------------

    state.on('forecastdata', _setUserGeo);
    state.on('init', _checkGeoPermission);

}
